import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { ApiCrudService } from './api-crud.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

export interface fileEditConfig {
  fileEdit: fileEdit[];
}

interface fileEdit {
  id: number;
  uuid?: string;
  dateModification: string;
}

export interface UploadedFile {
  id: number;
  uuid?: string;
  name: string;
  type: string;
  url: string;
  creationDate: Date;
  taille?: string;
  idRepertoire: number;
  dateModification?: string;
}

export interface Folder {
  idRepertoire: number;
  idRepertoireParent: number | null;
  nom: string;
}

export type ColumnItem =
  | { type: 'folder'; folder: Folder }
  | { type: 'file'; file: UploadedFile };

export interface Column {
  folders: Folder[];
  files: UploadedFile[];
  items: ColumnItem[];
  currentFolderId: number | null;
  sortBy: SortOption;
  sortOrder: 'asc' | 'desc';
}

export interface PreviewColumn {
  file: UploadedFile;
  currentFolderId: number;
  fileLoaded?: boolean;
}

export interface DragData {
  type: 'file' | 'folder';
  item: UploadedFile | Folder;
  sourceColumnIndex: number;
}

export type SortOption = 'name' | 'date' | 'size' | 'type';

@Injectable({
  providedIn: 'root'
})
export class FileService extends ApiCrudService<UploadedFile> {

  url = 'files';
  configUrl = 'conf/replacedFiles'

  constructor(
    protected http: HttpClient
  ) {
    super();
  }

  upload(data: FormData) {
    return this.http.post(this.url, data, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round(100 * event.loaded / event.total);
            return { status: 'progress', message: progress };
          case HttpEventType.Response:
            return { ...{ type: '' }, ...event.body };
          default:
            return 'Unhandled event ' + event.type;
        }
      })
    );
  }

  replaceFile(fileId: number, newFile: File, name?: string) {
    const formData = new FormData();
    formData.append('file', newFile);
    formData.append('name', name || newFile.name);
    formData.append('type', newFile.type)

    return this.http.put(`${this.url}/${fileId}`, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round(100 * event.loaded / event.total);
            return { status: 'progress', message: progress };
          case HttpEventType.Response:
            return { ...{ type: '' }, ...event.body };
          default:
            return 'Unhandled event ' + event.type;
        }
      })
    );
  }

  updateFileName(fileId: number, name: string) {
    return this.http.put(`${this.url}/${fileId}`, { name })
  }

  updateFileRepertoire(fileId: number, idRepertoire: number) {
    return this.http.put(`${this.url}/${fileId}`, { idRepertoire })
  }

  getFileIcon(type: string): string {
    if (type.includes('image')) {
      return "image";
    } else if (type.includes('video')) {
      return "video";
    } else if (type.includes('presentation') || type.includes('powerpoint')) {
      return "powerpoint";
    } else if (type.includes('word')) {
      return "word";
    } else if (type.includes('pdf')) {
      return "pdf";
    } else if (type.includes('ms-excel') || type.includes('spreadsheetml')) {
      return "excel";
    } else
      return "file";
  }

  getFolders(): Observable<Folder[]> {
    return this.http.get<Folder[]>('/repertoires');
  }

  createFolder(folder: Folder) {
    return this.http.post('repertoire', folder, this.httpOptions);
  }

  updateFolder(id: number, data: { nom: string, idRepertoireParent: number }) {
    return this.http.put(`repertoire/${id}`, data, this.httpOptions);
  }

  deleteFolder(id: number) {
    return this.http.delete(`repertoire/${id}`);
  }

  saveConfigFile(config: fileEditConfig) {
    return this.http.put<fileEditConfig>(this.configUrl, config);
  }

  getConfigFile() {
    return this.http.get<fileEditConfig>(this.configUrl);
  }

}
